import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  FiArrowLeft,
  FiCopy,
  FiEdit2,
  FiEye,
  FiLayers,
  FiPlus,
  FiRefreshCw,
  FiSearch,
  FiSlash,
  FiTarget
} from 'react-icons/fi';
import { assetClassTemplatesApi } from '../../api/smartTokenizerApi';
import {
  labelTokenizerPlatformCategoryId,
  mapAssetClassTemplateDtoToFrontend,
  mapCategoryToSector,
  TOKENIZER_PLATFORM_SECTORS
} from '../../api/smartTokenizerMappers';
import { useTokenIssuer } from '../../contexts/TokenIssuerContext';
import { useAssetClass } from '../../contexts/AssetClassContext';

const ManagerContainer = styled.div`
  max-width: 1600px;
  margin: 0 auto;
  padding: 2rem;
  min-height: 100vh;
  background: linear-gradient(135deg, #0f0f23 0%, #1a1a3e 100%);
`;

const Back = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #9bd6ff;
  text-decoration: none;
  margin-bottom: 1.5rem;
  font-weight: 500;
  &:hover {
    color: #c4e4ff;
  }
`;

const HeroSection = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  padding: 2rem 1rem;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
`;

const HeroTitle = styled.h1`
  font-size: 2.25rem;
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 0.75rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
`;

const HeroSubtitle = styled.p`
  font-size: 1.05rem;
  color: #a0a0a0;
  max-width: 720px;
  margin: 0 auto;
  line-height: 1.6;
`;

const ActionsSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 1rem;

  .icon {
    color: #4facfe;
  }
`;

const ActionButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: ${(props) =>
    props.primary ? 'linear-gradient(135deg, #4facfe, #00f2fe)' : 'rgba(255, 255, 255, 0.1)'};
  color: #fff;
  border: ${(props) => (props.primary ? 'none' : '1px solid rgba(255, 255, 255, 0.2)')};
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    transform: translateY(-2px);
    background: ${(props) =>
      props.primary ? 'linear-gradient(135deg, #3d8bfe, #00d4fe)' : 'rgba(255, 255, 255, 0.2)'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const ToolbarRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`;

const TemplatesSection = styled.div`
  margin-bottom: 3rem;
`;

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem 1.25rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
`;

const FilterSearchWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  min-width: 200px;
  max-width: 420px;
  color: #94a3b8;
  svg {
    flex-shrink: 0;
  }
`;

const FilterTextInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 0.6rem 0.75rem;
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  color: #fff;
  font-size: 0.95rem;
  &::placeholder {
    color: #64748b;
  }
  &:focus {
    outline: none;
    border-color: #4facfe;
  }
`;

const FilterSectorWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  label {
    color: #94a3b8;
    font-size: 0.9rem;
    white-space: nowrap;
  }
`;

const FilterSelect = styled.select`
  padding: 0.6rem 0.75rem;
  min-width: 200px;
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  color: #fff;
  font-size: 0.95rem;
  &:focus {
    outline: none;
    border-color: #4facfe;
  }
`;

const FilterMeta = styled.span`
  font-size: 0.85rem;
  color: #64748b;
  @media (min-width: 720px) {
    margin-left: auto;
  }
`;

const TemplatesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const TemplateCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 2rem;
  cursor: default;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    border-color: #4facfe;
    box-shadow: 0 10px 30px rgba(79, 172, 254, 0.1);
  }

  &.inactive {
    opacity: 0.72;
    border-style: dashed;
    border-color: rgba(251, 191, 36, 0.35);
  }

  .template-header {
    display: flex;
    justify-content: space-between;
    align-items: start;
    margin-bottom: 1rem;
  }

  .template-info {
    flex: 1;
  }

  .template-name {
    font-size: 1.3rem;
    font-weight: 600;
    color: #fff;
    margin-bottom: 0.5rem;
  }

  .template-category {
    color: #4facfe;
    font-size: 0.9rem;
    margin-bottom: 0.35rem;
  }

  .template-key {
    font-family: Consolas, Monaco, monospace;
    font-size: 0.75rem;
    color: #64748b;
    margin-bottom: 0.75rem;
  }

  .badge-inactive {
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #fbbf24;
    border: 1px solid rgba(251, 191, 36, 0.4);
    padding: 0.2rem 0.5rem;
    border-radius: 4px;
    align-self: flex-start;
  }

  .template-description {
    color: #a0a0a0;
    line-height: 1.5;
    margin-bottom: 1.5rem;
  }

  .template-specs {
    background: rgba(79, 172, 254, 0.05);
    border: 1px solid rgba(79, 172, 254, 0.2);
    border-radius: 8px;
    padding: 1rem;
    margin-bottom: 1rem;

    .specs-title {
      font-weight: 600;
      color: #4facfe;
      margin-bottom: 0.5rem;
      font-size: 0.9rem;
    }

    .specs-list {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
    }

    .spec-tag {
      background: rgba(79, 172, 254, 0.1);
      border: 1px solid rgba(79, 172, 254, 0.3);
      border-radius: 4px;
      padding: 0.25rem 0.5rem;
      font-size: 0.8rem;
      color: #4facfe;
    }
  }

  .template-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .template-action {
    padding: 0.5rem 1rem;
    background: rgba(79, 172, 254, 0.1);
    border: 1px solid rgba(79, 172, 254, 0.3);
    border-radius: 6px;
    color: #4facfe;
    font-size: 0.8rem;
    cursor: pointer;
    transition: all 0.3s ease;
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;

    &:hover {
      background: rgba(79, 172, 254, 0.2);
    }
  }
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;
`;

const FormModalSurface = styled.div`
  background: #15152d;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 16px;
  padding: 1.5rem;
  max-width: 640px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  color: #e2e8f0;
`;

const SchemaModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  flex-shrink: 0;
  padding-bottom: 1rem;
  margin-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  h2 {
    color: #fff;
    font-size: 1.35rem;
    margin: 0;
    line-height: 1.25;
  }

  .meta {
    color: #94a3b8;
    font-size: 0.85rem;
    margin-top: 0.4rem;
    line-height: 1.4;
  }
`;

const SchemaViewModalSurface = styled(motion.div)`
  background: #1a1a3e;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  width: 100%;
  max-width: 720px;
  max-height: min(88vh, 800px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 1.5rem 1.5rem 1.25rem;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.45);
`;

const SchemaScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(0, 0, 0, 0.45);
  scrollbar-width: thin;
  scrollbar-color: rgba(79, 172, 254, 0.45) rgba(255, 255, 255, 0.06);
`;

const SchemaCode = styled.pre`
  margin: 0;
  padding: 1.1rem 1.25rem 1.25rem;
  font-family: 'JetBrains Mono', 'Fira Code', 'Consolas', 'Monaco', monospace;
  font-size: 0.8125rem;
  line-height: 1.55;
  white-space: pre;
  tab-size: 2;
  color: #e2e8f0;

  .json-hl-key {
    color: #7dd3fc;
  }
  .json-hl-str {
    color: #86efac;
  }
  .json-hl-num {
    color: #fcd34d;
  }
  .json-hl-bool {
    color: #c4b5fd;
  }
  .json-hl-null {
    color: #94a3b8;
    font-style: italic;
  }
`;

const Field = styled.label`
  display: block;
  margin-bottom: 1rem;
  span {
    display: block;
    font-size: 0.8rem;
    color: #94a3b8;
    margin-bottom: 0.35rem;
  }
  input,
  select,
  textarea {
    width: 100%;
    padding: 0.55rem 0.75rem;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.15);
    background: rgba(0, 0, 0, 0.35);
    color: #fff;
    font-size: 0.9rem;
  }
  textarea {
    font-family: Consolas, Monaco, monospace;
    font-size: 0.78rem;
    min-height: 120px;
  }
`;

const Gate = styled.div`
  text-align: center;
  padding: 3rem;
  color: #fecaca;
  background: rgba(255, 80, 80, 0.08);
  border-radius: 16px;
  border: 1px solid rgba(255, 100, 100, 0.25);
`;

const EmptyTemplates = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #a0a0a0;
  grid-column: 1 / -1;
`;

function jsonStringToHighlightedHtml(json) {
  return json
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(
      /("(\\u[\dA-Fa-f]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
      (match) => {
        let cls = 'json-hl-num';
        if (/^"/.test(match)) {
          cls = /:$/.test(match) ? 'json-hl-key' : 'json-hl-str';
        } else if (/true|false/.test(match)) {
          cls = 'json-hl-bool';
        } else if (/null/.test(match)) {
          cls = 'json-hl-null';
        }
        return `<span class="${cls}">${match}</span>`;
      }
    );
}

const CLASS_TYPES = [
  { value: 'fungibleCommodity', label: 'Fungible commodity' },
  { value: 'standardizedSeries', label: 'Standardized series' },
  { value: 'uniqueCollectible', label: 'Unique collectible' },
  { value: 'fractionalAsset', label: 'Fractional asset (ERC-1155 style)' }
];

const DEFAULT_SCHEMA = `{
  "required": ["fieldA"],
  "specifications": {
    "fieldA": { "type": "string", "maxLength": 100 }
  }
}`;

function AdminAssetClassTemplates() {
  const { isAdmin } = useTokenIssuer();
  const { loadAssetClassTemplates } = useAssetClass();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [cloneSourceId, setCloneSourceId] = useState('');
  const [cloneKey, setCloneKey] = useState('');
  const [cloneName, setCloneName] = useState('');
  const [viewSchemaTemplate, setViewSchemaTemplate] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sectorFilter, setSectorFilter] = useState('');
  const schemaScrollRef = useRef(null);

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return rows.filter((apiRow) => {
      const template = mapAssetClassTemplateDtoToFrontend(apiRow);
      if (!template) return false;
      if (sectorFilter && template.category !== sectorFilter) return false;
      if (!q) return true;
      const name = String(apiRow.name ?? apiRow.Name ?? '').toLowerCase();
      const key = String(apiRow.templateKey ?? apiRow.TemplateKey ?? '').toLowerCase();
      const sectorLabel = labelTokenizerPlatformCategoryId(template.category).toLowerCase();
      const tu = String(template.tradingUnit ?? '').toLowerCase();
      const sym = String(template.marketSymbol ?? '').toLowerCase();
      const ct = String(template.classType ?? '').toLowerCase();
      return (
        name.includes(q) ||
        key.includes(q) ||
        sectorLabel.includes(q) ||
        tu.includes(q) ||
        sym.includes(q) ||
        ct.includes(q)
      );
    });
  }, [rows, searchQuery, sectorFilter]);

  const schemaViewPayload = useMemo(() => {
    if (!viewSchemaTemplate) {
      return { jsonStr: '', html: '' };
    }
    const jsonStr = JSON.stringify(
      {
        id: viewSchemaTemplate.id,
        standardSchema: viewSchemaTemplate.standardSchema,
        composability: viewSchemaTemplate.composability,
        tradingUnit: viewSchemaTemplate.tradingUnit,
        marketSymbol: viewSchemaTemplate.marketSymbol,
        classType: viewSchemaTemplate.classType,
        category: viewSchemaTemplate.category
      },
      null,
      2
    );
    return { jsonStr, html: jsonStringToHighlightedHtml(jsonStr) };
  }, [viewSchemaTemplate]);

  useLayoutEffect(() => {
    if (!viewSchemaTemplate) return;
    const el = schemaScrollRef.current;
    if (el) {
      el.scrollTop = 0;
      el.scrollLeft = 0;
    }
  }, [viewSchemaTemplate, schemaViewPayload.jsonStr]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await assetClassTemplatesApi.listAllAdmin();
      setRows(data || []);
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) {
      load();
    }
  }, [isAdmin, load]);

  const openCreate = () => {
    setForm({
      templateKey: '',
      name: '',
      categoryId: 'preciousMetals',
      tokenStandard: 'ERC721',
      classType: 'standardizedSeries',
      tradingUnit: 'unit',
      marketSymbol: '',
      metadataSchemaJson: DEFAULT_SCHEMA,
      composabilityJson: '',
      displayOrder: ''
    });
    setModal('create');
  };

  const openEdit = (apiRow) => {
    const m = mapAssetClassTemplateDtoToFrontend(apiRow);
    const categoryId = TOKENIZER_PLATFORM_SECTORS.some((s) => s.id === m.category)
      ? m.category
      : TOKENIZER_PLATFORM_SECTORS[0]?.id || 'realEstate';
    setForm({
      id: apiRow.id,
      name: m.name,
      categoryId,
      tokenStandard: m.tokenStandard,
      classType: m.classType,
      tradingUnit: m.tradingUnit,
      marketSymbol: m.marketSymbol,
      metadataSchemaJson: JSON.stringify(m.standardSchema || { required: [], specifications: {} }, null, 2),
      composabilityJson: m.composability ? JSON.stringify(m.composability, null, 2) : '',
      displayOrder: String(apiRow.displayOrder ?? ''),
      isActive: apiRow.isActive !== false
    });
    setModal('edit');
  };

  const submitCreate = async () => {
    try {
      const payload = {
        templateKey: form.templateKey.trim(),
        name: form.name.trim(),
        sector: mapCategoryToSector(form.categoryId),
        tokenStandard: form.tokenStandard,
        classType: form.classType,
        tradingUnit: form.tradingUnit.trim(),
        marketSymbol: form.marketSymbol.trim(),
        metadataSchemaJson: form.metadataSchemaJson.trim(),
        composabilityJson: form.composabilityJson?.trim() || null,
        displayOrder: form.displayOrder ? parseInt(form.displayOrder, 10) : null
      };
      await assetClassTemplatesApi.create(payload);
      toast.success('Template created');
      setModal(null);
      await load();
      await loadAssetClassTemplates();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Create failed');
    }
  };

  const submitEdit = async () => {
    try {
      const payload = {
        name: form.name.trim(),
        sector: mapCategoryToSector(form.categoryId),
        tokenStandard: form.tokenStandard,
        classType: form.classType,
        tradingUnit: form.tradingUnit.trim(),
        marketSymbol: form.marketSymbol.trim(),
        metadataSchemaJson: form.metadataSchemaJson.trim(),
        composabilityJson: form.composabilityJson?.trim() || null,
        displayOrder: form.displayOrder ? parseInt(form.displayOrder, 10) : null,
        isActive: form.isActive
      };
      await assetClassTemplatesApi.update(form.id, payload);
      toast.success('Template updated');
      setModal(null);
      await load();
      await loadAssetClassTemplates();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Update failed');
    }
  };

  const openCloneModal = (preselectedSourceId) => {
    setCloneSourceId(preselectedSourceId != null && preselectedSourceId !== '' ? String(preselectedSourceId) : '');
    setCloneKey('');
    setCloneName('');
    setModal('clone');
  };

  const submitClone = async () => {
    if (!cloneSourceId || !cloneKey.trim() || !cloneName.trim()) {
      toast.error('Select a source template, new key, and name');
      return;
    }
    try {
      await assetClassTemplatesApi.clone(cloneSourceId, {
        newTemplateKey: cloneKey.trim(),
        newName: cloneName.trim()
      });
      toast.success('Template cloned');
      setModal(null);
      setCloneSourceId('');
      setCloneKey('');
      setCloneName('');
      await load();
      await loadAssetClassTemplates();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Clone failed');
    }
  };

  const deactivate = async (id) => {
    if (!window.confirm('Deactivate this template? It will disappear from tokenizer asset class templates.')) {
      return;
    }
    try {
      await assetClassTemplatesApi.deactivate(id);
      toast.success('Template deactivated');
      await load();
      await loadAssetClassTemplates();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Deactivate failed');
    }
  };

  if (!isAdmin) {
    return (
      <ManagerContainer>
        <Back to="/">
          <FiArrowLeft /> Back to dashboard
        </Back>
        <Gate>
          <h2>Admin only</h2>
          <p>Sign in as an administrator to manage asset class templates.</p>
        </Gate>
      </ManagerContainer>
    );
  }

  return (
    <ManagerContainer>
      <Back to="/">
        <FiArrowLeft /> Back to dashboard
      </Back>

      <HeroSection>
        <HeroTitle>
          <FiLayers /> Asset class templates
        </HeroTitle>
        <HeroSubtitle>
          Same catalog that appears in <strong>Asset Class Manager → Asset Class Templates</strong>. Tokenizers create
          asset classes from these definitions; you can add, edit, clone, or deactivate entries here.
        </HeroSubtitle>
      </HeroSection>

      <TemplatesSection>
        <ActionsSection>
          <SectionTitle>
            <FiTarget className="icon" />
            Asset Class Templates
          </SectionTitle>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
            <ActionButton type="button" primary onClick={openCreate}>
              <FiPlus /> New template
            </ActionButton>
            <ActionButton type="button" onClick={() => openCloneModal()}>
              <FiCopy /> New from existing
            </ActionButton>
            <ActionButton type="button" onClick={load} disabled={loading}>
              <FiRefreshCw /> Refresh
            </ActionButton>
          </div>
        </ActionsSection>

        <FilterBar>
          <FilterSearchWrap>
            <FiSearch aria-hidden />
            <FilterTextInput
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search name, key, sector, symbol…"
              aria-label="Search templates"
            />
          </FilterSearchWrap>
          <FilterSectorWrap>
            <label htmlFor="asset-template-sector-filter">Sector</label>
            <FilterSelect
              id="asset-template-sector-filter"
              value={sectorFilter}
              onChange={(e) => setSectorFilter(e.target.value)}
            >
              <option value="">All sectors</option>
              {TOKENIZER_PLATFORM_SECTORS.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </FilterSelect>
          </FilterSectorWrap>
          {rows.length > 0 && (
            <FilterMeta>
              Showing {filteredRows.length} of {rows.length}
            </FilterMeta>
          )}
        </FilterBar>

        <TemplatesGrid>
          {loading ? (
            <EmptyTemplates>
              <FiLayers style={{ fontSize: '3rem', marginBottom: '1rem', color: '#4facfe' }} />
              <p>Loading templates…</p>
            </EmptyTemplates>
          ) : rows.length === 0 ? (
            <EmptyTemplates>
              <FiLayers style={{ fontSize: '3rem', marginBottom: '1rem', color: '#4facfe' }} />
              <h3 style={{ color: '#fff', marginBottom: '0.5rem' }}>No templates yet</h3>
              <p>Run DB migrations and restart the API (defaults seed on startup), or create a template above.</p>
            </EmptyTemplates>
          ) : filteredRows.length === 0 ? (
            <EmptyTemplates>
              <FiSearch style={{ fontSize: '3rem', marginBottom: '1rem', color: '#4facfe' }} />
              <h3 style={{ color: '#fff', marginBottom: '0.5rem' }}>No matching templates</h3>
              <p>Try a different search or set Sector to “All sectors”.</p>
            </EmptyTemplates>
          ) : (
            filteredRows.map((apiRow, index) => {
              const template = mapAssetClassTemplateDtoToFrontend(apiRow);
              const categoryLabel = labelTokenizerPlatformCategoryId(template.category);
              const required = template.standardSchema?.required || [];
              const isInactive = apiRow.isActive === false;

              return (
                <TemplateCard
                  key={apiRow.id}
                  className={isInactive ? 'inactive' : ''}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <div className="template-header">
                    <div className="template-info">
                      <div className="template-name">{template.name}</div>
                      <div className="template-category">{categoryLabel}</div>
                      <div className="template-key">{template.id}</div>
                    </div>
                    {isInactive && <span className="badge-inactive">Inactive</span>}
                  </div>

                  <div className="template-description">
                    Standardized {template.classType} for {template.tradingUnit || 'unit'}-based trading
                  </div>

                  <div className="template-specs">
                    <div className="specs-title">Required Fields</div>
                    <div className="specs-list">
                      {required.map((field) => (
                        <span key={field} className="spec-tag">
                          {field}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="template-actions">
                    <button
                      type="button"
                      className="template-action"
                      onClick={(e) => {
                        e.stopPropagation();
                        setViewSchemaTemplate(template);
                      }}
                    >
                      <FiEye /> View Schema
                    </button>
                    <button
                      type="button"
                      className="template-action"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEdit(apiRow);
                      }}
                    >
                      <FiEdit2 /> Edit
                    </button>
                    <button
                      type="button"
                      className="template-action"
                      onClick={(e) => {
                        e.stopPropagation();
                        openCloneModal(apiRow.id);
                      }}
                    >
                      <FiCopy /> New template from
                    </button>
                    {!isInactive && (
                      <button
                        type="button"
                        className="template-action"
                        onClick={(e) => {
                          e.stopPropagation();
                          deactivate(apiRow.id);
                        }}
                      >
                        <FiSlash /> Deactivate
                      </button>
                    )}
                  </div>
                </TemplateCard>
              );
            })
          )}
        </TemplatesGrid>
      </TemplatesSection>

      <AnimatePresence>
        {modal === 'create' && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setModal(null)}
          >
            <FormModalSurface onClick={(e) => e.stopPropagation()}>
              <h3 style={{ marginBottom: '1rem', color: '#fff' }}>Create template</h3>
              <Field>
                <span>Template key (unique, e.g. myGoldBarV2)</span>
                <input
                  value={form.templateKey}
                  onChange={(e) => setForm((f) => ({ ...f, templateKey: e.target.value }))}
                  placeholder="camelCaseKey"
                />
              </Field>
              <Field>
                <span>Display name</span>
                <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
              </Field>
              <Field>
                <span>Sector</span>
                <select
                  value={form.categoryId}
                  onChange={(e) => setForm((f) => ({ ...f, categoryId: e.target.value }))}
                >
                  {TOKENIZER_PLATFORM_SECTORS.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field>
                <span>Token standard</span>
                <select
                  value={form.tokenStandard}
                  onChange={(e) => setForm((f) => ({ ...f, tokenStandard: e.target.value }))}
                >
                  <option value="ERC721">ERC-721</option>
                  <option value="ERC1155">ERC-1155</option>
                </select>
              </Field>
              <Field>
                <span>Class type</span>
                <select
                  value={form.classType}
                  onChange={(e) => setForm((f) => ({ ...f, classType: e.target.value }))}
                >
                  {CLASS_TYPES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field>
                <span>Trading unit</span>
                <input
                  value={form.tradingUnit}
                  onChange={(e) => setForm((f) => ({ ...f, tradingUnit: e.target.value }))}
                />
              </Field>
              <Field>
                <span>Market symbol</span>
                <input
                  value={form.marketSymbol}
                  onChange={(e) => setForm((f) => ({ ...f, marketSymbol: e.target.value }))}
                />
              </Field>
              <Field>
                <span>Metadata schema JSON (required + specifications)</span>
                <textarea
                  value={form.metadataSchemaJson}
                  onChange={(e) => setForm((f) => ({ ...f, metadataSchemaJson: e.target.value }))}
                />
              </Field>
              <Field>
                <span>Composability JSON (optional)</span>
                <textarea
                  value={form.composabilityJson}
                  onChange={(e) => setForm((f) => ({ ...f, composabilityJson: e.target.value }))}
                />
              </Field>
              <ToolbarRow>
                <ActionButton type="button" primary onClick={submitCreate}>
                  Save
                </ActionButton>
                <ActionButton type="button" onClick={() => setModal(null)}>
                  Cancel
                </ActionButton>
              </ToolbarRow>
            </FormModalSurface>
          </ModalOverlay>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {modal === 'edit' && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setModal(null)}
          >
            <FormModalSurface onClick={(e) => e.stopPropagation()}>
              <h3 style={{ marginBottom: '1rem', color: '#fff' }}>Edit template</h3>
              <Field>
                <span>Display name</span>
                <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
              </Field>
              <Field>
                <span>Sector</span>
                <select
                  value={form.categoryId}
                  onChange={(e) => setForm((f) => ({ ...f, categoryId: e.target.value }))}
                >
                  {TOKENIZER_PLATFORM_SECTORS.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field>
                <span>Token standard</span>
                <select
                  value={form.tokenStandard}
                  onChange={(e) => setForm((f) => ({ ...f, tokenStandard: e.target.value }))}
                >
                  <option value="ERC721">ERC-721</option>
                  <option value="ERC1155">ERC-1155</option>
                </select>
              </Field>
              <Field>
                <span>Class type</span>
                <select
                  value={form.classType}
                  onChange={(e) => setForm((f) => ({ ...f, classType: e.target.value }))}
                >
                  {CLASS_TYPES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field>
                <span>Trading unit</span>
                <input
                  value={form.tradingUnit}
                  onChange={(e) => setForm((f) => ({ ...f, tradingUnit: e.target.value }))}
                />
              </Field>
              <Field>
                <span>Market symbol</span>
                <input
                  value={form.marketSymbol}
                  onChange={(e) => setForm((f) => ({ ...f, marketSymbol: e.target.value }))}
                />
              </Field>
              <Field>
                <span>Display order</span>
                <input
                  value={form.displayOrder}
                  onChange={(e) => setForm((f) => ({ ...f, displayOrder: e.target.value }))}
                />
              </Field>
              <Field>
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={Boolean(form.isActive)}
                    onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                  />
                  Active (visible to tokenizers)
                </span>
              </Field>
              <Field>
                <span>Metadata schema JSON</span>
                <textarea
                  value={form.metadataSchemaJson}
                  onChange={(e) => setForm((f) => ({ ...f, metadataSchemaJson: e.target.value }))}
                />
              </Field>
              <Field>
                <span>Composability JSON (optional)</span>
                <textarea
                  value={form.composabilityJson}
                  onChange={(e) => setForm((f) => ({ ...f, composabilityJson: e.target.value }))}
                />
              </Field>
              <ToolbarRow>
                <ActionButton type="button" primary onClick={submitEdit}>
                  Save
                </ActionButton>
                <ActionButton type="button" onClick={() => setModal(null)}>
                  Cancel
                </ActionButton>
              </ToolbarRow>
            </FormModalSurface>
          </ModalOverlay>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {modal === 'clone' && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setModal(null)}
          >
            <FormModalSurface onClick={(e) => e.stopPropagation()}>
              <h3 style={{ marginBottom: '1rem', color: '#fff' }}>New template from existing</h3>
              <Field>
                <span>Source template</span>
                <select value={cloneSourceId} onChange={(e) => setCloneSourceId(e.target.value)}>
                  <option value="">Select…</option>
                  {(rows || []).map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.templateKey} — {r.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field>
                <span>New template key (unique)</span>
                <input
                  value={cloneKey}
                  onChange={(e) => setCloneKey(e.target.value)}
                  placeholder="e.g. goldBarLBMA_custom"
                />
              </Field>
              <Field>
                <span>New display name</span>
                <input value={cloneName} onChange={(e) => setCloneName(e.target.value)} />
              </Field>
              <ToolbarRow>
                <ActionButton type="button" primary onClick={submitClone}>
                  <FiCopy /> Create copy
                </ActionButton>
                <ActionButton type="button" onClick={() => setModal(null)}>
                  Cancel
                </ActionButton>
              </ToolbarRow>
            </FormModalSurface>
          </ModalOverlay>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {viewSchemaTemplate && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setViewSchemaTemplate(null)}
          >
            <SchemaViewModalSurface
              initial={{ scale: 0.92, opacity: 0, y: 12 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.92, opacity: 0, y: 12 }}
              transition={{ type: 'spring', stiffness: 380, damping: 32 }}
              onClick={(e) => e.stopPropagation()}
            >
              <SchemaModalHeader>
                <div>
                  <h2>{viewSchemaTemplate.name}</h2>
                  <div className="meta">
                    {labelTokenizerPlatformCategoryId(viewSchemaTemplate.category)} · {viewSchemaTemplate.classType} ·{' '}
                    {viewSchemaTemplate.marketSymbol} · {viewSchemaTemplate.tradingUnit}
                  </div>
                </div>
                <ActionButton type="button" onClick={() => setViewSchemaTemplate(null)}>
                  Close
                </ActionButton>
              </SchemaModalHeader>
              <SchemaScrollArea ref={schemaScrollRef}>
                <SchemaCode dangerouslySetInnerHTML={{ __html: schemaViewPayload.html }} />
              </SchemaScrollArea>
            </SchemaViewModalSurface>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </ManagerContainer>
  );
}

export default AdminAssetClassTemplates;
